import * as React from 'react';
import ReactDOM from 'react-dom';
import {useDispatch} from 'react-redux';
import {Route, Redirect, Switch} from 'react-router-dom';
// @ts-expect-error
import ReactTooltip from 'react-tooltip';

import classNames from 'classnames';
import * as ramda from 'ramda';

import {convertToArray} from 'nutshell-core/filter';
import {Routing} from 'nutshell-core/routing';
import {SavedFilters} from 'nutshell-core/saved-filters';
import type {User} from 'nutshell-core/users/users-types';

import type {NutshellTour} from 'shells/tours/types';
import {useNutshellTour} from 'shells/tours/use-nutshell-tour';

import {readLocalStorage} from '../../util/localStorage';

import {PageWrapper} from '../layout/page-wrapper';

import {ActivityReport} from './activity/activity-report';
import {EmailReport} from './activity/email-report';
import {AttributionReport} from './attribution/attribution-report';
import {ChatbotReportPage} from './chatbot/chatbot-report-page';
import {EngagementReport} from './engagement/engagement-report';
import {FunnelReport} from './funnel-report/funnel';
import {isMissingRequired} from './is-missing-required';
import {LeadsReport} from './leads-reports/leads-report';
import {OverviewPage} from './overview/overview-page';
import {ReportAnalyticsPage} from './report-analytics-page';
import {
    LOSSES,
    NEW_LEADS,
    SALES,
    CUSTOM,
    FORECAST,
    FUNNEL,
    ACTIVITY,
    EMAIL,
    ATTRIBUTION,
    TASKS,
    ENGAGEMENT,
    CHATBOT,
    FALLBACK_FACET,
    SCHEDULER_REPORT,
    getReportDefaultState,
    getExportTypes,
    getPrimaryLeadsReportFilters,
    getReportBaseUrl,
    getShouldHydrateFromLocalStorage,
} from './report-constants';
import {ReportControlsContainer} from './report-controls-container';
import {ReportFilterDrawer} from './report-filter-drawer';
import {shouldShowReportControls} from './report-helpers';
import {ReportTitleBar} from './report-title-bar';
import type {ReportURLParams, ReportKey} from './report-types';
import {ReportViewTracker} from './report-view-tracker';
import {SchedulerReport} from './scheduler/scheduler-report';
import {Sidebar as ReportSidebar} from './sidebar';
import {TasksReport} from './tasks/tasks-report';
import {updateUrlParam} from './update-url-param';

const compMap = {
    [LOSSES]: LeadsReport,
    [NEW_LEADS]: LeadsReport,
    [SALES]: LeadsReport,
    [CUSTOM]: LeadsReport,
    [FORECAST]: LeadsReport,
    [ATTRIBUTION]: AttributionReport,
    [FUNNEL]: FunnelReport,
    [ACTIVITY]: ActivityReport,
    [EMAIL]: EmailReport,
    [TASKS]: TasksReport,
    [ENGAGEMENT]: EngagementReport,
    [CHATBOT]: ChatbotReportPage,
    [SCHEDULER_REPORT]: SchedulerReport,
} as const;

type Props = {
    user?: User | null | undefined;
    tour?: NutshellTour;
};

export function ReportsRoot(props: Props) {
    const {tour} = props;
    const dispatch = useDispatch();
    const {startTour} = useNutshellTour();

    React.useEffect(() => {
        // @ts-expect-error
        dispatch(SavedFilters.fetchSavedReports());

        const tooltipMount = document.createElement('div');
        if (document.body) {
            document.body.appendChild(tooltipMount);
            ReactDOM.render(
                <ReactTooltip
                    delayShow={300}
                    className='react-tooltip'
                    scrollHideSelector='#report-content-scroll-container'
                    effect='solid'
                />,
                tooltipMount
            );
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    React.useEffect(() => {
        let timeout = null;
        if (tour) {
            timeout = setTimeout(() => {
                startTour(tour);
            }, 2000);
        }

        return () => {
            if (timeout) {
                clearTimeout(timeout);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <PageWrapper sidebar={<ReportSidebar isSessionUserLoading={!props.user} />}>
            <PersistedRoute path='/reports/:report' componentMap={compMap} user={props.user} />
        </PageWrapper>
    );
}

type PersistedRouteProps = {
    componentMap: any;
    path: string;
    user: User | null | undefined;
};

const PersistedRoute = ({componentMap, path, user}: PersistedRouteProps) => (
    <Switch>
        <Route
            path='/reports/overview'
            render={() => {
                if (user && !user.permissions.canAccessCrm) {
                    return <Redirect to='/marketing/dashboard' />;
                }

                if (!NutClientConfig.hasReportGeneratorFF) {
                    return <Redirect to='/reports/sales/details' />;
                }

                return <OverviewPage />;
            }}
        />
        <Route
            path='/reports/snapshots'
            render={() => {
                if (!user || !user.permissions.canAccessCrm) {
                    return <Redirect to='/marketing/dashboard' />;
                }

                const pageBody = document.querySelector('body');
                if (pageBody && pageBody.id !== 'report-analytics-2017') {
                    pageBody.className = '';
                    pageBody.id = 'report-analytics-2017';
                }

                return <ReportAnalyticsPage user={user} />;
            }}
        />
        <Route
            path={path}
            render={(routeProps) => {
                // Note: the server will redirect if the report type is not valid
                const reportKey: ReportKey = routeProps.match.params.report as any;
                const defaults = getReportDefaultState(reportKey);

                const RoutedComponent = componentMap[reportKey];
                const localStorageKey = getReportBaseUrl(reportKey);
                const shouldHydrateFromLocalStorage = getShouldHydrateFromLocalStorage(reportKey);
                const persistToLocalStorageKey = shouldHydrateFromLocalStorage
                    ? localStorageKey
                    : undefined;

                if (routeProps.location.search === '') {
                    // Strip trailing slash if there is one
                    const currentPath = routeProps.location.pathname.endsWith('/')
                        ? routeProps.location.pathname.slice(0, -1)
                        : routeProps.location.pathname;

                    const {search: defaultQuery} = defaults;

                    // TODO (@ianvs): Load persisted query from server
                    const persistedQuery = shouldHydrateFromLocalStorage
                        ? readLocalStorage(localStorageKey)
                        : null;
                    if (persistedQuery) {
                        const prefixedPersistedQuery = persistedQuery.startsWith('?')
                            ? persistedQuery
                            : `?${persistedQuery}`;

                        return <Redirect to={`${currentPath}${prefixedPersistedQuery}`} />;
                    }

                    if (defaultQuery) {
                        return <Redirect to={`${currentPath}${defaultQuery}`} />;
                    }
                }

                const urlParamString = routeProps.location.search.slice(1); // Remove "?"
                const urlParams: ReportURLParams<'leads'> = Routing.deparam(urlParamString);
                const activeFilters = convertToArray(urlParams.filter);
                const paramChanger = updateUrlParam(
                    routeProps.location.pathname,
                    urlParams,
                    routeProps.history.replace,
                    persistToLocalStorageKey
                );

                // We renamed `leadBinTime` to `dateRangeType`, but still have to deal with
                // saved filters, so we'll just convert them here and "redirect"
                // @ts-expect-error
                if (urlParams.report && urlParams.report.leadBinTime) {
                    const newQuery = {
                        ...urlParams,
                        report: ramda.omit(['leadBinTime'], {
                            ...urlParams.report,
                            // @ts-expect-error
                            dateRangeType: urlParams.report.leadBinTime,
                            leadBinTime: undefined,
                        }),
                    } as const;

                    return (
                        <Redirect
                            to={`${routeProps.location.pathname}?${Routing.param(newQuery)}`}
                        />
                    );
                }

                // If an old url is hit, without report params, we need to add the defaults
                // This also adds the default sort to the url params
                if (
                    reportKey === ACTIVITY &&
                    // @ts-expect-error
                    (isMissingRequired(defaults.report, urlParams.report) ||
                        // @ts-expect-error
                        isMissingRequired(defaults.sort, urlParams.sort))
                ) {
                    const newQuery = {
                        ...urlParams,
                        // @ts-expect-error
                        report: {...defaults.report, ...urlParams.report},
                        // @ts-expect-error
                        sort: {...defaults.sort, ...urlParams.sort},
                    } as const;

                    return (
                        <Redirect
                            to={`${routeProps.location.pathname}?${Routing.param(newQuery)}`}
                        />
                    );
                }

                // If facet is missing from a lead report URL, fall back to default
                // AI generated reports sometimes produce the wrong param name (facetType instead of facet) and brick the page
                const needsFacet = [LOSSES, NEW_LEADS, SALES, CUSTOM, FORECAST].includes(reportKey);
                if (needsFacet && urlParams.report && !urlParams.report.facet) {
                    const newQuery = {
                        ...urlParams,
                        report: {...urlParams.report, facet: FALLBACK_FACET},
                    } as const;

                    return (
                        <Redirect
                            to={`${routeProps.location.pathname}?${Routing.param(newQuery)}`}
                        />
                    );
                }

                // The dateRangeType report param was added after reports had been saved off without it
                // so, if it doesn't exist, we need to add default dateRangeType and filters based on report type
                if (urlParams.report && !urlParams.report.dateRangeType) {
                    // @ts-expect-error
                    if (defaults.dateRangeType) {
                        // We changed the default dateRangeType on custom reports from closedTime to createdTime,
                        // so now old reports without a set `dateRangeType` will treat the closedTime
                        // as an actual filter.  So to keep them from getting an unwanted filter, we may want to strip it out
                        let sanitizedFilterParam = urlParams.filter;
                        // We only want to strip out default closedTime from the custom report.
                        // If the user changed the period and saved, that should be applied as a filter
                        if (reportKey === CUSTOM && urlParams.filter.closedTime === '-d365') {
                            // "-d365" was the old default closedTime for the date picker
                            sanitizedFilterParam = ramda.omit(['closedTime'], sanitizedFilterParam);
                        }
                        const newQuery = {
                            ...urlParams,
                            // @ts-expect-error
                            report: {...urlParams.report, dateRangeType: defaults.dateRangeType},
                            filter: {
                                ...sanitizedFilterParam,
                                // @ts-expect-error
                                [defaults.dateRangeType]: defaults.dateRangeTypeFilter,
                            },
                        } as const;

                        return (
                            <Redirect
                                to={`${routeProps.location.pathname}?${Routing.param(newQuery)}`}
                            />
                        );
                    }
                }

                const routedReportContentClassNames = classNames('routed-report-content', {
                    'routed-report-content--tasks': reportKey === 'tasks',
                });

                return (
                    <ReportViewTracker
                        reportKey={reportKey}
                        pathname={routeProps.location.pathname}
                        search={routeProps.location.search}
                        urlParams={urlParams}
                    >
                        <div className={routedReportContentClassNames}>
                            <ReportTitleBar
                                reportKey={reportKey}
                                urlParams={urlParams}
                                hasActionButton={Boolean(getExportTypes(reportKey).length)}
                                onParamChange={paramChanger}
                            />
                            {shouldShowReportControls(reportKey) && (
                                <ReportControlsContainer
                                    reportKey={reportKey}
                                    activeFilters={activeFilters}
                                    reportParams={urlParams.report}
                                    onParamChange={paramChanger}
                                />
                            )}
                            <div
                                className={classNames({
                                    'routed-report-inner-container--funnel': reportKey === FUNNEL,
                                    'routed-report-inner-container': reportKey !== FUNNEL,
                                    'routed-report-inner-container--no-controls':
                                        !shouldShowReportControls(reportKey) &&
                                        reportKey !== FUNNEL,
                                })}
                            >
                                {reportKey !== 'funnel' &&
                                    reportKey !== 'audienceMembership' &&
                                    reportKey !== 'editionEngagement' &&
                                    reportKey !== 'dripEditionEngagement' &&
                                    reportKey !== 'smsEditionEngagement' &&
                                    reportKey !== 'smsDripEditionEngagement' &&
                                    reportKey !== 'formEngagement' &&
                                    reportKey !== 'siteAnalytics' &&
                                    reportKey !== 'activity' &&
                                    reportKey !== 'mcfxcompanies' &&
                                    reportKey !== 'competitors' &&
                                    reportKey !== 'email' &&
                                    reportKey !== 'scheduler' &&
                                    reportKey !== 'landingPages' &&
                                    reportKey !== 'dashboard' &&
                                    reportKey !== 'engagementDashboard' &&
                                    reportKey !== 'tasks' &&
                                    reportKey !== 'engagement' &&
                                    reportKey !== 'quotes' &&
                                    reportKey !== 'invoices' &&
                                    reportKey !== 'widgetOpens' &&
                                    reportKey !== 'completedActions' &&
                                    reportKey !== 'visitors' &&
                                    reportKey !== 'engagementBarLeads' &&
                                    reportKey !== 'chatbot' &&
                                    reportKey !== 'schedulerReport' && (
                                        // Using string literals for flow refinement
                                        <ReportFilterDrawer
                                            reportKey={reportKey}
                                            listParams={urlParams.list}
                                            dateRangeType={urlParams.report.dateRangeType}
                                            entityType='leads'
                                            activeFilters={activeFilters}
                                            primaryFilters={getPrimaryLeadsReportFilters(reportKey)}
                                            onParamChange={paramChanger}
                                        />
                                    )}
                                <RoutedComponent
                                    {...routeProps}
                                    urlParams={urlParams}
                                    activeFilters={activeFilters}
                                    reportKey={reportKey}
                                    onParamChange={paramChanger}
                                    onParamPush={updateUrlParam(
                                        routeProps.location.pathname,
                                        urlParams,
                                        routeProps.history.push,
                                        persistToLocalStorageKey
                                    )}
                                />
                            </div>
                        </div>
                    </ReportViewTracker>
                );
            }}
        />
    </Switch>
);
